import crossFetch from 'cross-fetch';
import * as React from 'react';
import { getMfServerOverride } from '../lib/mf-utils.js';
import { getGlobalThis, isBrowser } from '../utils/index.js';
import { useMfProps } from './hooks.js';
import { useIsomorphicData } from './isomorphic-data-hook.js';
const fetch = getGlobalThis().fetch ? getGlobalThis().fetch : crossFetch;
const SVG_BASE64_DATA_PREFIX = 'data:image/svg+xml;base64,';
const SVG_URL_ENCODED_DATA_PREFIX = 'data:image/svg+xml,';
export const SvgSprite = ({ id, source, selfHosted = false }) => {
    if (isSvgBase64Data(source)) {
        const data = source.replace(SVG_BASE64_DATA_PREFIX, '');
        return React.createElement(SvgContainer, { id: id, content: decodeBase64(data) });
    }
    if (isSvgUrlEncodedData(source)) {
        const data = source.replace(SVG_URL_ENCODED_DATA_PREFIX, '');
        return React.createElement(SvgContainer, { id: id, content: decodeURIComponent(data) });
    }
    if (isSvgRawData(source)) {
        return React.createElement(SvgContainer, { id: id, content: source });
    }
    if (isReactComponent(source)) {
        const Component = source;
        return (React.createElement(SvgContainer, { id: id },
            React.createElement(Component, null)));
    }
    let svgFileUrl = isSvgFileUrl(source) ? source : undefined;
    if (svgFileUrl && !selfHosted) {
        const { mfId, mfServer } = useMfProps();
        const server = getMfServerOverride(mfServer);
        svgFileUrl = new URL(process.env.NODE_ENV === 'development' ? svgFileUrl : `/${mfId}${svgFileUrl}`, server).href;
    }
    const { data: svgFileContent } = useIsomorphicData({
        id,
        info: svgFileUrl,
        type: 'isolated',
    }, async (svgFileUrl) => svgFileUrl && loadSvg(svgFileUrl), () => {
        if (isBrowser()) {
            return new SpriteContainer(id).query()?.innerHTML;
        }
    });
    return svgFileContent ? React.createElement(SvgContainer, { id: id, content: svgFileContent }) : null;
};
const SvgContainer = ({ id, content, children, }) => {
    const htmlContent = {
        __html: content,
    };
    const container = new SpriteContainer(id);
    return content ? (React.createElement("div", { ...container.attrs(), dangerouslySetInnerHTML: content ? htmlContent : undefined })) : (React.createElement("div", { ...container.attrs() }, children));
};
async function loadSvg(url) {
    const resp = await fetch(url);
    return resp.text();
}
function isReactComponent(source) {
    return typeof source === 'function';
}
function isSvgFileUrl(source) {
    return typeof source === 'string' && source.endsWith('.svg');
}
function isSvgRawData(source) {
    return typeof source === 'string' && source.trim().startsWith('<svg');
}
function isSvgBase64Data(source) {
    return typeof source === 'string' && source.startsWith(SVG_BASE64_DATA_PREFIX);
}
function isSvgUrlEncodedData(source) {
    return typeof source === 'string' && source.startsWith(SVG_URL_ENCODED_DATA_PREFIX);
}
/**
 * Decode a string of base64 as text
 *
 * @param data The string of base64 encoded text
 * @returns The decoded text.
 */
function decodeBase64(data) {
    if (typeof atob === 'function') {
        return atob(data);
    }
    else if (typeof Buffer === 'function') {
        return Buffer.from(data, 'base64').toString('utf-8');
    }
    else {
        throw new Error('Failed to determine the platform specific decoder');
    }
}
class SpriteContainer {
    constructor(id) {
        this.id = id;
    }
    attrs() {
        return {
            'data-sprite-id': this.id,
            'data-type': 'svg-sprite',
            type: 'application/json',
            style: { width: 0, height: 0, position: 'absolute', overflow: 'hidden' },
        };
    }
    query() {
        return document.querySelector(`div[data-sprite-id="${this.id}"][data-type="svg-sprite"]`);
    }
}
SpriteContainer.id = 'data-sprite-id';
//# sourceMappingURL=SvgSprite.js.map